import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/auth-store";
import {
  getAuthorizedApps,
  getNavItems,
  getAppByPath,
  PermissionChecker,
  getUniqueEntitiesFromPermissions,
} from "@/lib/utils";
import { App, EntityInstanceDto, NavItem } from "@/lib/types";

interface AppContextValue {
  currentApp: App | null;
  setCurrentApp: (app: App) => void;
  apps: App[];
  navItems: NavItem[];
  entities: EntityInstanceDto[];
  actions: string | string[];
  selectedEntity: EntityInstanceDto | null;
  handleEntityChange: (entityInstance: EntityInstanceDto) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { user, isAuthenticated, hasEntityAction, isInGroup } = useAuthStore();

  const permissionChecker: PermissionChecker = useMemo(
    () => ({ hasEntityAction, isInGroup }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasEntityAction, isInGroup, user?.permissions, user?.groups] // Depend on actual permission data
  );

  const authorizedApps = useMemo(
    () => (isAuthenticated ? getAuthorizedApps(permissionChecker) : []),
    [isAuthenticated, permissionChecker]
  );

  const [currentApp, setCurrentApp] = useState<App | null>(() =>
    getAppByPath(location.pathname, authorizedApps)
  );

  // Raw, unfiltered list of every entity the user has any permission on.
  const allEntities = useMemo(() => {
    if (!user?.permissions) return [];
    return getUniqueEntitiesFromPermissions(user.permissions);
  }, [user?.permissions]);

  // Scoped to the current app: each entity carries an `entity_type`
  // that matches the App's `entityType` override (falling back to its
  // `key`). Get Ready entities live under `entity_type: "dealership"`
  // on the backend so its `entityType` override is set explicitly;
  // Garage Register and Cleanup keep using their app key directly.
  // When `currentApp` is null (e.g. /login, /home), fall back to the
  // full list.
  const entities = useMemo(() => {
    if (!currentApp) return allEntities;
    const scope = currentApp.entityType ?? currentApp.key;
    return allEntities.filter((e) => e.entity_type === scope);
  }, [allEntities, currentApp]);

  const actions = useMemo(() => {
    if (!user?.permissions) return [];
    return Array.from(
      new Set(user.permissions.flatMap((p) => p.actions || []))
    );
  }, [user?.permissions]);

  console.log("🔍 User actions in AppProvider:", actions);

  const [selectedEntity, setSelectedEntity] = useState<EntityInstanceDto | null>(() => {
    // Try to load from localStorage on initialization
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('selectedEntity');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {
          return null;
        }
      }
    }
    return null;
  });

  // Keep `selectedEntity` in sync with the current app's filtered list.
  // Triggers on: initial mount, login (permissions arrive), or app
  // switch (entities filter shrinks/grows). When the saved selection
  // belongs to a different app's entity_type the user has now navigated
  // away from, snap to the first entity of the new app (or null).
  useEffect(() => {
    if (entities.length === 0) {
      // No entities for the current app — clear the saved selection so
      // we don't carry a stale one into the next visit.
      if (selectedEntity !== null) {
        if (typeof window !== "undefined") {
          localStorage.removeItem("selectedEntity");
        }
        setSelectedEntity(null);
      }
      return;
    }
    const stillValid = selectedEntity
      ? entities.find(
          (e) =>
            e.entity_id === selectedEntity.entity_id &&
            e.entity_type === selectedEntity.entity_type
        )
      : null;
    if (!stillValid) {
      const first = entities[0];
      if (typeof window !== "undefined") {
        localStorage.setItem("selectedEntity", JSON.stringify(first));
      }
      setSelectedEntity(first);
    }
  }, [entities, selectedEntity]);

  // Handle entity selection
  const handleEntityChange = (entity: EntityInstanceDto) => {
    // Store in localStorage for persistence
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedEntity', JSON.stringify(entity));
    }
    setSelectedEntity(entity);
  };

  // Update current app when location changes
  useEffect(() => {
    const app = getAppByPath(location.pathname, authorizedApps);
    if (app !== currentApp) {
      setCurrentApp(app);
    }
  }, [location.pathname, authorizedApps, currentApp]);

  const navItems = useMemo(
    () => (currentApp ? getNavItems(currentApp, permissionChecker) : []),
    [currentApp, permissionChecker]
  );

  return (
    <AppContext.Provider
      value={{
        currentApp,
        setCurrentApp,
        apps: authorizedApps,
        entities,
        navItems,
        selectedEntity,
        handleEntityChange,
        actions,
      }}
    >
      {children}
    </AppContext.Provider>

  );
};


export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within AppProvider");
  }
  return context;
};
